<template>
    <div
        class="flex items-start gap-3 | print:hidden"
        :class="[
            isEmbeddedSurface || !useOuterPadding ? 'p-0' : 'p-3 xl:p-6',
            isElevatedSurface ? 'border-b border-gray-200 bg-gray-50' : 'bg-white',
        ]"
    >
        <note-avatar class="hidden sm:block" :user="authUser" v-if="showInputAvatar" />
        <div
            data-testid="note-composer-card"
            class="flex-1 flex flex-col bg-white relative overflow-hidden"
            :class="[
                isEmbeddedSurface ? '' : 'rounded-lg',
                isEmbeddedSurface ? '' : isBorderedSurface ? 'border border-gray-200' : 'shadow-md',
            ]"
        >
            <p
                :id="externalBlockDescriptionId"
                class="m-0 border-b border-gray-200 px-3 py-2 text-sm text-gray-600"
                data-testid="note-external-block-reason"
                v-if="externallyBlocked"
            >
                {{ externalBlockReason }}
            </p>
            <note-context
                :icon="noteContextIcon"
                :message="noteContextMessage"
                :show-cancel="!showFooterCancel"
                :value="isReplying ? contextNote.content : ''"
                @cancel="onCancel"
                v-if="isReplying || isEditing"
            />
            <button
                class="text-gray-600 absolute z-40 top-0 right-0 p-2 | hover:text-gray-700"
                @click="onCancel"
                v-tooltip="cancelButtonTooltip"
                v-else-if="isActive"
            >
                <icon name="bold/remove-circle" />
            </button>

            <div class="w-full h-24" v-show="audioOpened">
                <audio-recorder
                    class="z-10 relative"
                    :opened="audioOpened"
                    :comment="isEditing ? contextNote : bufferNote"
                    @input="handleAudioFile"
                    @is-recording="handleIsRecording"
                    @error-recording="audioOpened = false"
                />
            </div>

            <audio-player
                :file="bufferNote.video"
                :editing="true"
                :resolve-url="isEditing ? resolveAudioUrl : null"
                class="w-full"
                v-if="bufferNote.video"
            />

            <template v-if="isTextNote">
                <div>
                    <note-mention-editor
                        :class="{ 'rounded-t-lg': !isEmbeddedSurface }"
                        :aria-label="$t('clientCard.addCommentOrUpload')"
                        :aria-describedby="
                            externallyBlocked ? externalBlockDescriptionId : undefined
                        "
                        :aria-disabled="externallyBlocked"
                        :disabled="isSaving || verifiedSale"
                        :model-value="bufferNote.content"
                        :placeholder="$t('clientCard.addCommentOrUpload')"
                        :users="users"
                        ref="mentionEditor"
                        @mention-selected="onMentionSelected"
                        @submit="onSave"
                        @update:model-value="updateContent"
                        v-if="isEditing && usesInlineMentions"
                    />
                    <textarea
                        class="p-3 w-full block"
                        :class="{ 'rounded-t-lg': !isEmbeddedSurface }"
                        :aria-label="$t('clientCard.addCommentOrUpload')"
                        :aria-describedby="
                            externallyBlocked ? externalBlockDescriptionId : undefined
                        "
                        :aria-disabled="externallyBlocked ? 'true' : undefined"
                        :readonly="externallyBlocked"
                        :placeholder="$t('clientCard.addCommentOrUpload')"
                        :value="bufferNote.content"
                        ref="textArea"
                        @input="onContentInput"
                        @keydown.meta.enter="onSave"
                        v-else-if="isEditing"
                    />
                    <dropzone @file-dropped="setFiles" v-else>
                        <note-mention-editor
                            :class="{ 'rounded-t-lg': !isEmbeddedSurface }"
                            :aria-label="$t('clientCard.addCommentOrUpload')"
                            :aria-describedby="
                                externallyBlocked ? externalBlockDescriptionId : undefined
                            "
                            :aria-disabled="externallyBlocked"
                            :disabled="isSaving || verifiedSale"
                            :model-value="bufferNote.content"
                            :placeholder="$t('clientCard.addCommentOrUpload')"
                            :users="users"
                            ref="mentionEditor"
                            @mention-selected="onMentionSelected"
                            @submit="onSave"
                            @update:model-value="updateContent"
                            v-if="usesInlineMentions"
                        />
                        <textarea
                            class="p-3 w-full block"
                            :class="{ 'rounded-t-lg': !isEmbeddedSurface }"
                            :aria-label="$t('clientCard.addCommentOrUpload')"
                            :aria-describedby="
                                externallyBlocked ? externalBlockDescriptionId : undefined
                            "
                            :aria-disabled="externallyBlocked ? 'true' : undefined"
                            :readonly="externallyBlocked"
                            :placeholder="$t('clientCard.addCommentOrUpload')"
                            :value="bufferNote.content"
                            ref="textArea"
                            @input="onContentInput"
                            @keydown.meta.enter="onSave"
                            v-else
                        />
                    </dropzone>
                </div>
            </template>

            <div
                data-testid="note-composer-toolbar"
                class="flex flex-wrap justify-between items-center gap-3 p-3 border-t border-gray-200"
                :class="[
                    composerClass,
                    { 'rounded-b-lg': !isEmbeddedSurface },
                    isElevatedSurface ? 'bg-gray-100' : 'bg-white',
                    { 'note-toolbar--big': iconStyle === 'big' },
                ]"
            >
                <div data-testid="note-toolbar-left" class="flex grow min-w-0 items-center gap-3">
                    <mention-input
                        :account="account"
                        :aria-describedby="
                            externallyBlocked ? externalBlockDescriptionId : undefined
                        "
                        :aria-disabled="externallyBlocked"
                        :disabled="isSaving"
                        :disabled-tooltip="externalBlockReason"
                        :selected-item-ids="bufferNote.alert_users"
                        :users="users"
                        @update:selected-item-ids="updateAlertUsers"
                        v-if="usesButtonMentions"
                    />

                    <activix-tooltip
                        :content="recordingButtonTooltip"
                        :show-on-mobile="externallyBlocked"
                    >
                        <span class="inline-flex">
                            <activix-button
                                data-testid="note-recording-button"
                                class="note-action-btn"
                                :aria-describedby="
                                    externallyBlocked ? externalBlockDescriptionId : undefined
                                "
                                :aria-disabled="externallyBlocked"
                                :disabled="isSaving || isEditing || hasText"
                                @click="microphoneClick"
                            >
                                <icon name="regular/microphone" />
                            </activix-button>
                        </span>
                    </activix-tooltip>

                    <activix-tooltip
                        data-testid="note-attachment-tooltip"
                        :content="fileUploadTooltip"
                        :show-on-mobile="externallyBlocked"
                    >
                        <span class="inline-flex">
                            <activix-button
                                data-testid="note-attachment-button"
                                class="note-action-btn"
                                :class="{ 'cursor-not-allowed': externallyBlocked }"
                                :aria-describedby="
                                    externallyBlocked ? externalBlockDescriptionId : undefined
                                "
                                :aria-disabled="externallyBlocked ? 'true' : undefined"
                                :disabled="!fileUploadActive && !externallyBlocked"
                                @click="openFilePicker"
                            >
                                <input
                                    :id="`file_${noteKey}`"
                                    class="hidden z-10 relative"
                                    :class="{ 'cursors-not-allowed': audioOpened }"
                                    multiple="multiple"
                                    :name="`file_${noteKey}`"
                                    type="file"
                                    data-testid="note-file-input"
                                    ref="file"
                                    @change="uploadAttachments()"
                                    v-if="fileUploadActive"
                                />
                                <icon
                                    name="regular/attachment"
                                    :class="{
                                        disabled: !fileUploadActive,
                                        'text-blue-500': hasFiles,
                                    }"
                                />
                                <span
                                    class="pl-1 text-xs"
                                    data-testid="note-attachment-count"
                                    v-if="hasFiles"
                                >
                                    {{ bufferNote.file.length }}
                                </span>
                            </activix-button>
                        </span>
                    </activix-tooltip>

                    <activix-tooltip
                        :content="privacyTooltip"
                        data-testid="note-visibility-toggle"
                        :show-on-mobile="externallyBlocked"
                        v-if="eyePosition === 'left'"
                    >
                        <div class="flex items-center justify-center space-x-1.5">
                            <icon class="text-gray-600" :name="privateNoteIcon" />
                            <activix-switcher
                                color="blue"
                                size="x-small"
                                :aria-label="$t('clientCard.privateNote')"
                                :aria-describedby="
                                    externallyBlocked ? externalBlockDescriptionId : undefined
                                "
                                :aria-disabled="externallyBlocked"
                                :disabled="isSaving"
                                :value="bufferNote.private"
                                @input="updatePrivacy"
                            />
                        </div>
                    </activix-tooltip>

                    <div
                        class="flex grow items-center"
                        v-if="!audioFile && !bufferNote.video && bufferNote.file_url"
                    >
                        <a class="flex items-center" :href="bufferNote.file_url" target="_blank">
                            <icon name="regular/common-file-download" />
                            <span class="ml-2 truncate max-w-32 2xl:max-w-64">{{
                                getFileName(bufferNote.file_url)
                            }}</span>
                        </a>
                        <icon
                            class="link-grey-light ml-3 | hover:text-red-500"
                            :name="$icons.trash"
                            @click="removeExistingAttachment"
                        />
                    </div>
                </div>

                <activix-tooltip
                    :content="privacyTooltip"
                    data-testid="note-visibility-toggle"
                    :show-on-mobile="externallyBlocked"
                    v-if="eyePosition === 'right'"
                >
                    <div class="flex items-center justify-center space-x-1.5">
                        <icon class="text-gray-600" :name="privateNoteIcon" />
                        <activix-switcher
                            color="blue"
                            size="x-small"
                            :aria-label="$t('clientCard.privateNote')"
                            :aria-describedby="
                                externallyBlocked ? externalBlockDescriptionId : undefined
                            "
                            :aria-disabled="externallyBlocked"
                            :disabled="isSaving"
                            :value="bufferNote.private"
                            @input="updatePrivacy"
                        />
                    </div>
                </activix-tooltip>

                <activix-tooltip
                    data-testid="note-submit-tooltip"
                    class="ml-auto"
                    :content="composerDisabledReason"
                    :show-on-mobile="externallyBlocked"
                >
                    <div class="flex items-center gap-3">
                        <activix-button
                            data-testid="note-cancel"
                            @click="onCancel"
                            v-if="showFooterCancel && (isEditing || isReplying)"
                        >
                            {{ $t('general.cancel') }}
                        </activix-button>

                        <activix-button
                            data-testid="note-submit"
                            :aria-describedby="
                                externallyBlocked ? externalBlockDescriptionId : undefined
                            "
                            :aria-disabled="externallyBlocked"
                            :disabled="
                                isSaving || (!externallyBlocked && (saveDisabled || verifiedSale))
                            "
                            :loading="isSaving"
                            color="blue"
                            @click="onSave"
                        >
                            {{ resolvedSubmitLabel }}
                        </activix-button>
                    </div>
                </activix-tooltip>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, useId } from 'vue';
    import { mapState } from 'pinia';
    import { useLayoutStore } from '@/store/modules/layout/store.js';
    import { useUserStore } from '@/store/modules/user/store.js';

    import Vapor from 'laravel-vapor';
    import { ActivixButton } from '@/components/activix-ui/index.ts';

    import AudioPlayer from '@/components/audio/AudioPlayer.vue';
    import AudioRecorder from '@/components/audio/AudioRecorder.vue';
    import NoteAvatar from '@/components/container/lead/notes/NoteAvatar.vue';
    import Dropzone from '@/components/container/input/Dropzone.vue';
    import MentionInput from '@/components/container/input/note/MentionInput.vue';
    import NoteMentionEditor from '@/components/container/lead/notes/NoteMentionEditor.vue';
    import NoteContext from '@/components/container/lead/notes/NoteContext.vue';

    import Account from '@/entities/Account.js';
    import Comment from '@/entities/Comment.ts';
    import NoteAction from '@/entities/NoteAction.js';
    import Role from '@/entities/Role.js';
    import Lead from '@/entities/Lead.js';
    import { NoteSubmitType } from '@/lead/types/LeadNoteTypes.ts';
    import { NoteComposerSurface } from '@/components/container/lead/notes/NoteComposerSurface.ts';
    import { NoteMentionMode } from '@/components/container/lead/notes/NoteMentionMode.ts';

    import { useNoteMentions } from '@/hooks/mention/useNoteMentions.ts';
    import useTextareaAutosize from '@/hooks/useTextareaAutosize.ts';

    export default {
        compatConfig: { WATCH_ARRAY: false },

        components: {
            ActivixButton,
            AudioPlayer,
            AudioRecorder,
            Dropzone,
            MentionInput,
            NoteAvatar,
            NoteContext,
            NoteMentionEditor,
        },

        emits: ['cancel', 'submit'],

        setup() {
            const textArea = ref(null);
            const { resize } = useTextareaAutosize(textArea, { minHeight: 84, maxHeight: 350 });
            const noteMentions = useNoteMentions();
            const externalBlockDescriptionId = useId();

            return { externalBlockDescriptionId, noteMentions, textArea, resize };
        },

        props: {
            account: {
                type: Account,
                required: true,
            },
            contextNote: {
                type: Comment,
                default: null,
            },
            noteAction: {
                type: String,
                default: NoteAction.CREATE,
                validator: (value) => NoteAction.values.includes(value),
            },
            users: {
                type: Array,
                required: true,
            },
            lead: {
                type: Lead,
                required: true,
            },
            // --- Presentation (defaults render an elevated, avatar-led composer) ---
            // Show the author avatar beside the composer.
            showInputAvatar: {
                type: Boolean,
                default: true,
            },
            // Visual ownership: elevated is the legacy card, bordered is a flat standalone
            // card, and embedded lets the parent note card own the border and radius.
            surface: {
                type: String,
                default: NoteComposerSurface.ELEVATED,
                validator: (value) => Object.values(NoteComposerSurface).includes(value),
            },
            // Keep the composer card surface while removing the wrapper spacing.
            useOuterPadding: {
                type: Boolean,
                default: true,
            },
            // Toolbar icon-button density: 'compact' (default) or 'big'.
            iconStyle: {
                type: String,
                default: 'compact',
                validator: (value) => ['compact', 'big'].includes(value),
            },
            // Side the visibility (eye) toggle sits on within the toolbar.
            eyePosition: {
                type: String,
                default: 'right',
                validator: (value) => ['left', 'right'].includes(value),
            },
            // Mention affordance: 'button' renders the legacy mention picker;
            // 'inline' uses the atomic @mention editor.
            mentionMode: {
                type: String,
                default: NoteMentionMode.BUTTON,
                validator: (value) => Object.values(NoteMentionMode).includes(value),
            },
            // Extra classes appended to the toolbar/footer surface for free styling.
            composerClass: {
                type: [String, Array, Object],
                default: '',
            },
            // Submit-button label; empty falls back to the generic "save" wording.
            submitLabel: {
                type: String,
                default: '',
            },
            // Optional edit-mode label override for embedded contexts that need more
            // specific copy than the historical generic "Editing" label.
            editingLabel: {
                type: String,
                default: '',
            },
            // Render edit cancellation in the footer beside Save instead of only inside
            // the context row.
            showFooterCancel: {
                type: Boolean,
                default: false,
            },
            // External save-in-flight signal for controller-owned composers. It is the
            // source of truth whenever submitHandler is provided; event-driven legacy
            // composers continue using their existing `$wait` keys.
            saving: {
                type: Boolean,
                default: false,
            },
            // Async write seam. When provided, it becomes the sole dispatch path: the
            // built note is handed to it and the composer resets only once it resolves,
            // so a rejected write leaves the typed draft in place for a retry. When
            // absent, the composer keeps the fire-and-forget `submit` event and resets
            // immediately — the write lifecycle then lives entirely with the consumer.
            submitHandler: {
                type: Function,
                default: null,
            },
            actionBlock: {
                type: Object,
                default: undefined,
            },
        },

        data() {
            return {
                audioFile: null,
                audioOpened: false,
                bufferNote: new Comment(),
                isRecording: false,
            };
        },

        computed: {
            ...mapState(useLayoutStore, ['bodySpacingTop', 'headerHeight']),

            authUser() {
                return useUserStore().authUser;
            },

            isBorderedSurface() {
                return this.surface === NoteComposerSurface.BORDERED;
            },

            isElevatedSurface() {
                return this.surface === NoteComposerSurface.ELEVATED;
            },

            isEmbeddedSurface() {
                return this.surface === NoteComposerSurface.EMBEDDED;
            },

            resolvedSubmitLabel() {
                return this.submitLabel || this.$t('clientCard.save');
            },

            verifiedSale() {
                return !!this.lead.disabledBySaleVerified(this.authUser);
            },
            verifiedSaleReason() {
                return this.verifiedSale ? this.$t('clientCard.verifiedSaleUneditable') : '';
            },

            externallyBlocked() {
                return Boolean(this.actionBlock);
            },

            externalBlockReason() {
                return this.actionBlock?.reason || '';
            },

            composerDisabledReason() {
                return this.externalBlockReason || this.verifiedSaleReason;
            },

            privacyTooltip() {
                return this.externalBlockReason || this.$t('clientCard.privateNote');
            },

            alertUsersWithoutGroups() {
                return this.finalAlertUsers.filter((userId) => !String(userId).includes('G'));
            },

            finalAlertUsers() {
                return this.noteMentions.reconcileRecipients(
                    this.bufferNote.content || '',
                    this.bufferNote.alert_users
                );
            },

            cancelButtonTooltip() {
                if (this.audioOpened) {
                    return this.$t('clientCard.recordDelete');
                }

                return '';
            },

            fileUploadActive() {
                return (
                    this.isTextNote && !this.isEditing && !this.isSaving && !this.externallyBlocked
                );
            },

            fileUploadTooltip() {
                if (this.externallyBlocked) {
                    return this.externalBlockReason;
                }

                if (this.isEditing) {
                    return this.$t('clientCard.boxes.notes.notAvailableInEditMode');
                }

                return this.$t('clientCard.attachFiles');
            },

            hasFiles() {
                return this.bufferNote?.file?.length > 0;
            },

            hasText() {
                return this.bufferNote.content !== '';
            },

            hasUpdatedMentions() {
                if (!this.isEditing) {
                    return true;
                }

                const originalMentions = this.contextNote.alert_users;
                const updatedMentions = this.bufferNote.alert_users;

                if (updatedMentions.length !== originalMentions.length) {
                    return true;
                }

                return updatedMentions.some((userId) => !originalMentions.includes(userId));
            },

            isAdding() {
                return [NoteAction.CREATE, NoteAction.REPLY].includes(this.noteAction);
            },

            isActive() {
                return (
                    this.audioOpened ||
                    this.audioFile ||
                    this.bufferNote.content ||
                    this.bufferNote.file?.length ||
                    this.isEditing ||
                    this.isReplying
                );
            },

            isEditing() {
                return this.noteAction === NoteAction.EDIT;
            },

            isReplying() {
                return this.noteAction === NoteAction.REPLY;
            },

            isSaving() {
                if (this.submitHandler) {
                    return this.saving;
                }

                return this.$wait.is('creating.note') || this.$wait.is('updating.note');
            },

            isTextNote() {
                return !this.audioFile && !this.audioOpened && !this.bufferNote.video;
            },

            usesButtonMentions() {
                return this.mentionMode === NoteMentionMode.BUTTON;
            },

            usesInlineMentions() {
                return this.mentionMode === NoteMentionMode.INLINE;
            },

            noteContextMessage() {
                if (this.isReplying) {
                    const contextFullname = this.contextNote.fullname || '';
                    const [contextFirstName = '', contextLastName = ''] = contextFullname.split(
                        ' ',
                        2
                    );

                    if (!contextLastName || contextLastName === '-') {
                        return `${this.$t('clientCard.replyingTo')} ${contextFirstName}`;
                    }

                    const contextUsername = `${contextFirstName
                        .charAt(0)
                        .toUpperCase()}. ${contextLastName}`;

                    return `${this.$t('clientCard.replyingTo')} ${contextUsername}`;
                }

                if (this.isEditing) {
                    return this.editingLabel || this.$t('clientCard.editing');
                }

                return '';
            },

            noteContextIcon() {
                if (this.isReplying) {
                    return 'regular/email-action-reply';
                }

                if (this.isEditing) {
                    return 'regular/pencil-1';
                }

                return '';
            },

            noteKey() {
                if (!this.bufferNote.parent_id) {
                    return 'new';
                }

                return `${this.bufferNote.parent_id}_${this.bufferNote.id || 'new'}`;
            },

            privateNoteIcon() {
                if (this.bufferNote.private) {
                    return 'regular/view-off';
                }

                return 'regular/view-1';
            },

            recordingButtonTooltip() {
                if (this.externallyBlocked) {
                    return this.externalBlockReason;
                }

                if (this.isEditing) {
                    return this.$t('clientCard.boxes.notes.notAvailableInEditMode');
                }

                if (this.hasText) {
                    return this.$t('clientCard.cantAddAudioNoteWithText');
                }

                return this.$t('clientCard.record');
            },

            saveDisabled() {
                if (this.isSaving || this.externallyBlocked) {
                    return true;
                }

                if (this.isAdding) {
                    return (
                        !this.bufferNote.content &&
                        empty(this.bufferNote.file) &&
                        empty(this.audioFile)
                    );
                }

                return (
                    this.bufferNote.private === this.contextNote.private &&
                    this.bufferNote.content === this.contextNote.content &&
                    !this.hasUpdatedMentions
                );
            },
        },

        watch: {
            audioOpened(newValue) {
                if (newValue) {
                    this.bufferNote.private = this.authUser.role_id === Role.COMMERCIAL;
                } else {
                    this.bufferNote.private = false;
                }
            },

            'bufferNote.alert_users': {
                deep: true,
                handler() {
                    if (
                        !this.bufferNote.parent_user_id ||
                        this.bufferNote.parent_user_id === this.authUser.id
                    ) {
                        return;
                    }

                    if (
                        !this.bufferNote.alert_users.some(
                            (userId) => userId === this.bufferNote.parent_user_id
                        )
                    ) {
                        this.bufferNote.alert_users.push(this.bufferNote.parent_user_id);
                    }
                },
            },

            contextNote() {
                if (this.isEditing) {
                    this.bufferNote = new Comment({ ...this.contextNote });
                } else if (this.isReplying) {
                    this.bufferNote = new Comment({
                        parent_id: this.contextNote.id,
                        parent_user_id: this.contextNote.user_id,
                    });
                } else {
                    this.bufferNote = new Comment();
                }

                this.syncInlineMentionedIds();
            },
        },

        methods: {
            focus() {
                if (this.externallyBlocked) {
                    return;
                }

                if (this.mentionMode === NoteMentionMode.INLINE) {
                    if (!this.$refs.mentionEditor) {
                        return;
                    }

                    this.$refs.mentionEditor.focus();
                } else {
                    const textArea = this.$refs.textArea;

                    if (!textArea) {
                        return;
                    }

                    textArea.focus({ preventScroll: true });
                }

                const currentScrollTop = window.scrollY;
                const itemPosition = this.$el.getBoundingClientRect().top + currentScrollTop;
                const mainHeaderOffset = this.headerHeight + this.bodySpacingTop;
                const newScrollTop = itemPosition - mainHeaderOffset;

                if (newScrollTop >= currentScrollTop) {
                    return;
                }

                window.scroll({
                    top: newScrollTop,
                    behavior: 'smooth',
                });
            },

            handleAudioFile(file) {
                if (this.externallyBlocked) {
                    return;
                }

                this.audioFile = file;
            },

            resolveAudioUrl() {
                return this.$signedUrl.resolve('audioNote', this.bufferNote.id);
            },

            onMentionSelected(userId) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.alert_users = this.noteMentions.addMentionRecipient(
                    this.bufferNote.alert_users,
                    userId
                );
            },

            syncInlineMentionedIds() {
                if (this.mentionMode === NoteMentionMode.INLINE) {
                    this.noteMentions.syncFromContent(this.bufferNote.content || '');
                    return;
                }

                this.noteMentions.reset();
            },

            handleIsRecording(status) {
                if (this.externallyBlocked) {
                    return;
                }

                this.isRecording = status;
            },

            microphoneClick() {
                if (this.externallyBlocked) {
                    return;
                }

                this.setFiles([]);
                this.audioOpened = !this.audioOpened;
            },

            openFilePicker() {
                if (!this.fileUploadActive) {
                    return;
                }

                this.$refs.file.click();
            },

            onCancel() {
                this.resetData();

                this.$emit('cancel');
            },

            onSave() {
                if (this.saveDisabled) {
                    return;
                }

                if (this.isEditing) {
                    this.sendEditedNote();
                } else {
                    this.sendNote();
                }
            },

            resetData() {
                this.audioFile = null;
                this.audioOpened = false;
                this.bufferNote = new Comment();
                this.noteMentions.reset();
                this.$nextTick(() => {
                    if (this.mentionMode === NoteMentionMode.BUTTON) {
                        this.resize();
                    }
                });
            },

            // Hand one built note to the consumer without touching composer state. With a
            // submitHandler that handler is the sole dispatch path; the promise it returns
            // lets a caller await the write and decide whether to reset. Without one the note
            // is emitted (the consumer owns the write lifecycle) and this resolves at once.
            // Resolves true on a successful write, false when the handler rejected.
            async submitNote(intent) {
                if (this.externallyBlocked) {
                    return false;
                }

                if (!this.submitHandler) {
                    this.$emit('submit', intent);
                    return true;
                }

                try {
                    await this.submitHandler(intent);
                } catch {
                    // The consumer surfaced the failure; report it so the draft is kept.
                    return false;
                }

                return true;
            },

            // Route a single built note to the consumer, resetting the composer only once the
            // write succeeds. A rejected write keeps the draft for a retry.
            async dispatchNote(note, type) {
                if (this.externallyBlocked) {
                    return false;
                }

                const succeeded = await this.submitNote({ type, note });
                if (succeeded) {
                    this.resetData();
                }

                return succeeded;
            },

            // Route each attached file as its own note, one write per file. Stops at the first
            // rejection so a failed batch keeps the draft (and its remaining files) for a
            // retry, and resets once after every file has been written.
            async dispatchNotePerFile(note, files) {
                if (this.externallyBlocked) {
                    return;
                }

                for (const [index, file] of files.entries()) {
                    // eslint-disable-next-line no-await-in-loop -- sequential so one failure halts the batch
                    const succeeded = await this.submitNote({
                        type: NoteSubmitType.CREATE,
                        note: { ...note, file },
                    });

                    if (!succeeded) {
                        this.bufferNote.file = files.slice(index);
                        return;
                    }
                }

                this.resetData();
            },

            async sendAudioFile() {
                if (this.externallyBlocked) {
                    return;
                }

                const duration = Math.floor(this.audioFile.duration);
                const audioFile = new File([this.audioFile.blobs], `${this.audioFile.name}.mp3`);

                const responseVapor = await Vapor.store(audioFile, {
                    progress: (progress) => {
                        this.uploadProgress = Math.round(progress * 100);
                    },
                    baseURL: this.$axios.defaults.baseURL,
                    headers: this.$axios.defaults.headers.common,
                });

                await this.dispatchNote(
                    {
                        parentId: this.bufferNote.parent_id,
                        key: responseVapor.key,
                        alert_users: this.alertUsersWithoutGroups,
                        private: this.bufferNote.private,
                        duration,
                    },
                    NoteSubmitType.AUDIO
                );
            },

            sendNote() {
                if (this.externallyBlocked) {
                    return;
                }

                if (this.audioFile) {
                    this.sendAudioFile();
                } else {
                    let note = {
                        parent_id: this.bufferNote.parent_id,
                        alert_users: this.alertUsersWithoutGroups,
                        alert: this.bufferNote.alert,
                        alert_sms: this.bufferNote.alert_sms,
                        alert_email: this.bufferNote.alert_email,
                        content: this.bufferNote.content,
                        file: this.bufferNote.file,
                        private: this.bufferNote.private,
                        user_id: this.authUser.id,
                        request_user_id: this.bufferNote.request_user_id,
                    };

                    if (note.file?.length === 1) {
                        note = {
                            ...note,
                            file: note.file[0],
                            content: note.content,
                        };
                    } else if (note.file?.length > 1) {
                        this.dispatchNotePerFile(note, [...note.file]);

                        return;
                    }

                    this.dispatchNote(note, NoteSubmitType.CREATE);
                }
            },

            sendEditedNote() {
                if (this.externallyBlocked) {
                    return;
                }

                const noteData = {
                    id: this.bufferNote.id,
                    content: this.bufferNote.content,
                    file_url: this.bufferNote.file_url,
                    private: this.bufferNote.private,
                    alert_users: this.alertUsersWithoutGroups,
                };

                this.dispatchNote(noteData, NoteSubmitType.UPDATE);
            },

            setFiles(files) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.file = [...files];
            },

            uploadAttachments(attachment) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.file = attachment || this.$refs.file.files;
                this.bufferNote.file = [...this.bufferNote.file];
            },

            updateContent(content) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.content = content;
            },

            onContentInput(event) {
                if (this.externallyBlocked) {
                    event.target.value = this.bufferNote.content;
                    return;
                }

                this.updateContent(event.target.value);
            },

            updateAlertUsers(userIds) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.alert_users = userIds;
            },

            updatePrivacy(isPrivate) {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.private = isPrivate;
            },

            removeExistingAttachment() {
                if (this.externallyBlocked) {
                    return;
                }

                this.bufferNote.file_url = '';
            },
        },

        created() {
            // The contextNote watch is intentionally non-immediate, so an instance mounted
            // straight into edit/reply mode would otherwise start empty. Seed bufferNote from
            // the initial context here.
            if (this.isEditing && this.contextNote) {
                this.bufferNote = new Comment({ ...this.contextNote });
            } else if (this.isReplying && this.contextNote) {
                this.bufferNote = new Comment({
                    parent_id: this.contextNote.id,
                    parent_user_id: this.contextNote.user_id,
                });
            }

            this.syncInlineMentionedIds();
        },
    };
</script>

<style scoped>
    /*
     * 'big' icon style: compact square boxed toolbar buttons with a subtle shadow.
     * The toolbar buttons are ActivixButton instances, so reach them with :deep().
     */
    .note-toolbar--big :deep(.note-action-btn) {
        height: 28px;
        width: 28px;
        padding: 0;
        border-radius: 4px;
    }
</style>
